import { MbscCalendarEventData } from '@akoom-tech/mobiscroll-react';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import Iconify from '@/components/Iconify';
import { convertTo24HourFormat } from '../../utils';

type MbscScheduleEventProps = {
  data: MbscCalendarEventData;
  awaitingAcceptance?: boolean;
  durationLabel?: string | null;
  timeRangeLabel?: string;
};

const AWAITING_ACCEPTANCE_ACCENT = '#B91C1C';
const AWAITING_ACCEPTANCE_TEXT = '#7F1D1D';
const AWAITING_ACCEPTANCE_SUBTLE_TEXT = '#991B1B';

export default function MbscScheduleEvent({
  data,
  awaitingAcceptance = false,
  durationLabel,
  timeRangeLabel,
}: MbscScheduleEventProps) {
  const theme = useTheme();
  const property = data.original?.property;
  const showPropertyBadge = Boolean(property?.type && property?.icon);
  const useIconOwnColors = String(property?.icon ?? '').startsWith('logos:');
  const serviceLabel = data.original?.serviceLabel ?? 'N/A';
  const isHolidayRentalEvent =
    property?.icon === 'logos:airbnb-icon' || serviceLabel === 'HO';
  const hasArrivalRiskWarning = Boolean(data.original?.hasArrivalRiskWarning);
  const badgeBackground =
    awaitingAcceptance
      ? theme.palette.common.white
      : serviceLabel === 'HO'
      ? theme.palette.error.main
      : theme.palette.info.main;
  const badgeColor = awaitingAcceptance
    ? AWAITING_ACCEPTANCE_ACCENT
    : theme.palette.common.white;
  const timeRange =
    timeRangeLabel ??
    `${convertTo24HourFormat(data.start)}-${convertTo24HourFormat(data.end)}`;
  const timeLabel = durationLabel
    ? `${timeRange} (${durationLabel})`
    : timeRange;
  const eventTextColor = awaitingAcceptance
    ? AWAITING_ACCEPTANCE_TEXT
    : theme.palette.text.primary;
  const eventSubtleTextColor = awaitingAcceptance
    ? AWAITING_ACCEPTANCE_SUBTLE_TEXT
    : theme.palette.text.secondary;

  return (
    <Stack spacing={0.45} sx={{ minWidth: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        spacing={0.75}
        sx={{ minWidth: 0 }}
      >
        <Typography
          fontSize={13}
          fontWeight={800}
          lineHeight={1.15}
          noWrap
          sx={{ minWidth: 0, color: eventTextColor }}
        >
          {data.title}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={0.35}
          sx={{ flexShrink: 0, minWidth: 0 }}
        >
          {hasArrivalRiskWarning && (
            <Box
              data-testid="scheduler-arrival-risk-icon"
              sx={{
                width: 18,
                height: 18,
                borderRadius: 0.5,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                color: theme.palette.error.dark,
                backgroundColor: theme.palette.common.white,
                boxShadow: `0 0 0 1px ${theme.palette.error.light}`,
              }}
            >
              <Iconify icon="eva:alert-triangle-outline" width={14} />
            </Box>
          )}

          {showPropertyBadge && (
            <Tooltip title={property?.iconTitle ?? property?.type} arrow>
              <Box
                sx={{
                  backgroundColor: theme.palette.common.white,
                  width: 18,
                  height: 18,
                  borderRadius: 0.5,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  boxShadow: `0 0 0 1px ${theme.palette.grey[300]}`,
                }}
              >
                <Iconify
                  icon={property?.icon ?? 'eva:question-mark-outline'}
                  width={isHolidayRentalEvent ? 16 : 13}
                  color={useIconOwnColors ? undefined : 'black'}
                />
              </Box>
            </Tooltip>
          )}

          {awaitingAcceptance && (
            <Tooltip title="Awaiting acceptance" arrow placement="top">
              <Box
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.common.white,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 0 0 1px ${theme.palette.common.white}`,
                  flexShrink: 0,
                }}
              >
                <HourglassEmptyRoundedIcon
                  sx={{
                    fontSize: 12,
                    color: AWAITING_ACCEPTANCE_ACCENT,
                  }}
                />
              </Box>
            </Tooltip>
          )}

          <Box
            component="span"
            data-testid={
              isHolidayRentalEvent
                ? 'scheduler-holiday-service-badge'
                : 'scheduler-service-badge'
            }
            sx={{
              height: 20,
              minWidth: 28,
              px: 0.55,
              borderRadius: 0.75,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: badgeColor,
              backgroundColor: badgeBackground,
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1,
              boxShadow: awaitingAcceptance
                ? `0 0 0 1px ${theme.palette.common.white}`
                : `0 1px 2px ${theme.palette.grey[300]}`,
            }}
          >
            {serviceLabel}
          </Box>
        </Stack>
      </Stack>

      <Typography
        fontSize={12}
        lineHeight={1.15}
        noWrap
        sx={{ color: eventSubtleTextColor }}
      >
        {timeLabel}
      </Typography>

      <Typography
        fontSize={13.5}
        lineHeight={1.15}
        fontWeight={800}
        sx={{
          color: eventTextColor,
          overflowWrap: 'anywhere',
          wordBreak: 'normal',
        }}
      >
        {data.original?.address.postcode || 'N/A'}
      </Typography>
    </Stack>
  );
}
